<template>
    <VScroll class="request-inventory">
        <div class="request-inventory__control">
            <VButton
                class="button_green-light request-inventory__toggle"
                type="button"
                v-if="!isPickerOpen"
                @click="openPicker"
            >
                Привязать оборудование
            </VButton>

            <!-- Поиск — тем же API, что и список раздела инвентаря: область просмотра
                 и поиск по серийнику уже там, второй механизм не нужен. -->
            <div class="request-inventory__picker" v-else>
                <div class="request-inventory__search">
                    <VInput
                        class="request-inventory__search-input"
                        placeholder="Название или серийный номер"
                        :value="search"
                        @input="onSearch"
                    ></VInput>

                    <button class="request-inventory__close" type="button" @click="closePicker">Отмена</button>
                </div>

                <p class="request-inventory__hint" v-if="isSearching">Поиск…</p>
                <p class="request-inventory__hint" v-else-if="searchError">{{ searchError }}</p>
                <p class="request-inventory__hint" v-else-if="search && !results.length">Ничего не найдено</p>

                <ul class="request-inventory__results" v-else-if="results.length">
                    <li class="request-inventory__result" :key="row.id" v-for="row in resultRows">
                        <div class="request-inventory__result-body">
                            <span class="request-inventory__name">{{ row.name }}</span>
                            <span class="request-inventory__meta">
                                № позиции {{ row.id }} · S/N {{ row.serial }} · {{ row.city }}
                            </span>
                        </div>

                        <span class="request-inventory__linked" v-if="row.isLinked">Привязано</span>
                        <VButton
                            class="button_green request-inventory__attach"
                            type="button"
                            v-else
                            :progress="pendingId === row.id"
                            :disabled="pendingId !== null"
                            @click="attach(row)"
                        >
                            Привязать
                        </VButton>
                    </li>
                </ul>
            </div>
        </div>

        <div class="request-inventory__loader" v-if="isLoading">
            <VSpinner></VSpinner>
        </div>

        <template v-else>
            <p class="request-inventory__error" v-if="error">{{ error }}</p>

            <p class="request-inventory__empty" v-else-if="!rows.length">Оборудование не привязано</p>

            <ul class="request-inventory__list" v-else>
                <li class="request-inventory__item" :key="row.id" v-for="row in rows">
                    <router-link class="request-inventory__item-link" :to="itemLink(row)">
                        <span class="request-inventory__name">{{ row.name }}</span>
                        <span class="request-inventory__meta">
                            № позиции {{ row.id }} · S/N {{ row.serial }} · {{ row.city }}
                        </span>
                        <span class="request-inventory__status" :style="{ '--status-color': row.statusColor }">
                            {{ row.statusTitle }}
                        </span>
                    </router-link>

                    <button
                        class="request-inventory__remove"
                        type="button"
                        title="Отвязать"
                        :disabled="pendingId !== null"
                        @click="confirmDetach(row)"
                    >
                        <TrashSvg></TrashSvg>
                    </button>
                </li>
            </ul>
        </template>
    </VScroll>
</template>
<script>
// Components
import VButton from '@/views/components/VButton/VButton.vue';
import VInput from '@/views/components/VInput/VInput.vue';
import VScroll from '@/views/components/VScroll/VScroll.vue';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';

import TrashSvg from '@/images/trash.svg';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';
import ModalNames from '@/js/enums/ModalNames';
import { attachRequestInventoryItem, detachRequestInventoryItem, getRequestInventoryItems } from '@/js/api';
import { paginateInventoryItems } from '@/js/inventory-api';
import { errorMessage } from '@/js/error';
import { linkedItemRow, pickerRows } from '@/js/requestInventory';

/**
 * Размер выдачи поиска. Человек ищет конкретную вещь по серийнику или названию;
 * длинный список здесь значит, что запрос надо уточнить, а не листать.
 */
const SEARCH_LIMIT = 10;

/**
 * Вкладка «Оборудование» карточки обращения (7-G1).
 *
 * Показывается только при праве просмотра инвентаря — это решает RequestView. Доступ
 * к конкретным позициям не вычисляется здесь вовсе: список бэкенд уже сузил до области
 * просмотра, а привязку позиции вне области он отвергнет сам.
 *
 * Привязка не меняет предмет и не пишет ни историю обращения, ни жизненный цикл вещи —
 * поэтому ни право правки инвентаря, ни комментарий здесь не спрашиваются.
 */
@Component({
    components: {
        VButton,
        VInput,
        VScroll,
        VSpinner,
        TrashSvg,
    },
})
class RequestViewTabInventory extends Vue {
    @Prop({ type: Object, required: true })
    request;

    items = [];
    isLoading = false;
    error = '';

    isPickerOpen = false;
    search = '';
    results = [];
    isSearching = false;
    searchError = '';

    pendingId = null;

    // Номер последнего fetch() — см. там же.
    fetchToken = 0;

    get rows() {
        return this.items.map(linkedItemRow);
    }

    get resultRows() {
        return pickerRows(this.results, this.items);
    }

    itemLink(row) {
        return { name: 'inventory.items', params: { id: row.id } };
    }

    async fetch() {
        // Принимается только ответ последнего запроса. Иначе при быстром переходе A → B
        // запоздавший список A ложился бы на обращение B: его позиции помечались бы
        // как уже привязанные, и привязать их к B было бы нельзя.
        const token = ++this.fetchToken;

        this.isLoading = true;
        this.error = '';

        try {
            const response = await getRequestInventoryItems(this.request.id);

            if (token !== this.fetchToken) {
                return;
            }

            this.items = Array.isArray(response.data) ? response.data : [];
        } catch (requestError) {
            if (token !== this.fetchToken) {
                return;
            }

            this.error = errorMessage(requestError);
            this.items = [];
        } finally {
            if (token === this.fetchToken) {
                this.isLoading = false;
            }
        }
    }

    openPicker() {
        this.isPickerOpen = true;
    }

    closePicker() {
        this.isPickerOpen = false;
        this.search = '';
        this.results = [];
        this.searchError = '';
        this.debouncedSearch.cancel();
    }

    onSearch(value) {
        this.search = value;

        if (!String(value).trim()) {
            this.debouncedSearch.cancel();
            this.results = [];
            this.isSearching = false;

            return;
        }

        this.isSearching = true;
        this.debouncedSearch(value);
    }

    async runSearch(value) {
        this.searchError = '';

        try {
            const response = await paginateInventoryItems(1, String(value).trim(), SEARCH_LIMIT);

            // Ответ устаревшего запроса не должен перетирать выдачу по новому тексту.
            if (value !== this.search) {
                return;
            }

            this.results = Array.isArray(response.data.data) ? response.data.data : [];
        } catch (requestError) {
            this.searchError = errorMessage(requestError);
            this.results = [];
        } finally {
            if (value === this.search) {
                this.isSearching = false;
            }
        }
    }

    async attach(row) {
        if (this.pendingId !== null || row.isLinked) {
            return;
        }

        this.pendingId = row.id;

        try {
            await attachRequestInventoryItem(this.request.id, row.id);
            await this.fetch();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.pendingId = null;
        }
    }

    confirmDetach(row) {
        this.$showModal(ModalNames.CONFIRM, {
            title: 'Отвязать оборудование',
            text: `Отвязать «${row.name}» от обращения? Само оборудование не изменится.`,
            cancelTitle: 'Отмена',
            confirmTitle: 'Отвязать',
            confirmAction: () => this.detach(row),
        });
    }

    async detach(row) {
        this.pendingId = row.id;

        try {
            await detachRequestInventoryItem(this.request.id, row.id);
            await this.fetch();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.pendingId = null;
        }
    }

    @Watch('request.id')
    onRequestChanged() {
        this.closePicker();
        this.fetch();
    }

    created() {
        this.debouncedSearch = debounce(this.runSearch, 300);
    }

    mounted() {
        this.fetch();
    }

    beforeDestroy() {
        this.debouncedSearch.cancel();
    }
}

export default RequestViewTabInventory;
</script>
<style lang="scss">
.request-inventory {
    &__control,
    &__list,
    &__loader,
    &__empty,
    &__error {
        margin: 24px;
    }

    &__loader {
        display: flex;
        justify-content: center;
    }

    &__empty,
    &__hint {
        color: #9aa0a6;
        font-size: 0.875rem;
    }

    &__hint {
        margin: 12px 0 0;
    }

    &__error {
        color: var(--error-color);
        font-size: 0.875rem;
    }

    &__search {
        display: flex;
        gap: 12px;
        align-items: center;
    }

    &__search-input {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__close {
        flex: 0 0 auto;
        color: #5f6368;
        font-size: 0.875rem;
    }

    &__results,
    &__list {
        padding: 0;
        list-style: none;
    }

    &__results {
        margin: 12px 0 0;
    }

    &__result,
    &__item {
        display: flex;
        gap: 12px;
        align-items: center;
        padding: 12px 16px;
        border: 1px solid #e4eaef;
        border-radius: 12px;
    }

    &__result + &__result,
    &__item + &__item {
        margin-top: 8px;
    }

    &__result-body,
    &__item-link {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        color: inherit;
        text-decoration: none;
    }

    &__item-link:hover &__name {
        text-decoration: underline;
    }

    &__name {
        color: #202124;
        font-size: 0.9375rem;
        font-weight: 600;
        overflow-wrap: anywhere;
    }

    &__meta {
        color: #5f6368;
        font-size: 0.8125rem;
        overflow-wrap: anywhere;
    }

    &__status {
        color: var(--status-color, #5f6368);
        font-size: 0.8125rem;
        font-weight: 500;
    }

    &__linked {
        flex: 0 0 auto;
        color: #9aa0a6;
        font-size: 0.8125rem;
    }

    &__attach {
        flex: 0 0 auto;
    }

    &__remove {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;
        padding: 6px;
        fill: var(--error-color);

        &:disabled {
            opacity: 0.4;
        }
    }
}
</style>
